import { t } from '@lingui/core/macro';
import type { BrandInsightsRecord } from '@navio/nsdk';
import { NavioAreaChart, NoddiSegmentedControl, NoddiSkeleton, useNoddiQueryParam } from 'noddi-ui-web';
import { createEnumParam, withDefault } from 'use-query-params';

import { getTabsTranslations } from '../../utils/commonTranslations';
import { formatBucketLabel, formatCompactMoney, formatCount } from './brandInsightsFormat';

const METRICS = ['revenue', 'bookings'] as const;
type Metric = (typeof METRICS)[number];
const DEFAULT_METRIC: Metric = 'revenue';
const MetricParam = withDefault(createEnumParam<Metric>([...METRICS]), DEFAULT_METRIC);

interface Props {
  /** Undefined while the period loads. */
  insights?: BrandInsightsRecord;
  comparisonLabel: string;
}

/** The period's chart; the lower half of the overview card, with the metric switch right above what it drives. */
export const BrandInsightsTrend = ({ insights, comparisonLabel }: Props) => {
  const [metric, setMetric] = useNoddiQueryParam('metric', MetricParam);

  return (
    <section className='flex flex-col gap-4 border-t border-border pt-6'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div className='flex flex-col gap-1'>
          <h2 className='text-base font-semibold'>{t`Development`}</h2>
          <p className='text-sm text-muted-foreground'>{t`By the day the customer booked, not the day of the service`}</p>
        </div>
        <NoddiSegmentedControl
          ariaLabel={t`Chart metric`}
          value={metric}
          onValueChange={setMetric}
          translations={getTabsTranslations()}
          options={[
            { value: 'revenue', label: t`Revenue` },
            { value: 'bookings', label: t`Bookings` }
          ]}
        />
      </div>
      {insights ? (
        <TrendChart insights={insights} metric={metric} comparisonLabel={comparisonLabel} />
      ) : (
        <NoddiSkeleton className='h-64 w-full' />
      )}
    </section>
  );
};

const TrendChart = ({
  insights,
  metric,
  comparisonLabel
}: {
  insights: BrandInsightsRecord;
  metric: Metric;
  comparisonLabel: string;
}) => {
  const { granularity, previousSeries } = insights;
  const { currency } = insights.totals.revenue;
  // Buckets line up by position: the comparison span has the same length and granularity.
  const data = insights.series.map((point, index) => {
    const previous = previousSeries?.[index];
    return {
      label: point.bucketStart,
      value: metric === 'revenue' ? point.revenue.amount : point.bookings,
      comparisonValue: previous && (metric === 'revenue' ? previous.revenue.amount : previous.bookings)
    };
  });

  return (
    <NavioAreaChart
      className='aspect-auto h-64 w-full'
      data={data}
      valueLabel={metric === 'revenue' ? t`Revenue` : t`Bookings`}
      comparisonLabel={previousSeries ? comparisonLabel : undefined}
      labelFormatter={(label) => formatBucketLabel(label, granularity)}
      valueFormatter={metric === 'revenue' ? (amount) => formatCompactMoney(amount, currency) : formatCount}
    />
  );
};
